<template>
  <section class="payload-analytics-report-wrapper">
    <!-- Top Hero Banner / Page Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1">
      <div>
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="TruckIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Payload Analytics & Operational Telemetry') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Advanced monitoring of transport cycles, estimated weighing, compliance and operational sites') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center mt-1 mt-md-0"
        style="gap: 8px;"
      >
        <!-- Botão Guia de Interpretação -->
        <b-button
          v-b-tooltip.hover
          variant="outline-info"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('See methodology and indicators')"
          @click="openGuideModal(0)"
        >
          <feather-icon
            icon="HelpCircleIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('How to Interpret') }}</span>
          <span class="d-inline d-sm-none">{{ $t('Help') }}</span>
        </b-button>

        <b-button
          v-if="assetId"
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-if="assetId"
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          @click="fetchData"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isLoadingSummary || isLoadingCycles || isLoadingDetails || loadingAsset }"
          />
        </b-button>

        <b-button
          v-if="detailsData.data && detailsData.data.length > 0"
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('Export cycles from this page to CSV')"
          @click="exportToCsv"
        >
          <feather-icon
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Export CSV') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Compact Asset Info Header Card (Inspired by weeklyWorkStatus.vue) -->
    <b-card
      v-if="assetId"
      no-body
      class="asset-header-card mb-2 shadow-sm"
    >
      <div class="asset-header-content p-1 p-md-1">
        <!-- Skeleton Loading State -->
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center justify-content-between flex-wrap w-100 py-50"
        >
          <div class="d-flex align-items-center">
            <b-spinner
              variant="primary"
              small
              class="mr-1"
            />
            <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
          </div>
        </div>

        <!-- Loaded Content -->
        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <!-- Left: Back Button, Avatar, Plate, Marca, Modelo -->
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <!-- Back Button -->
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <!-- Vehicle Avatar -->
            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || 'Asset'"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <!-- Vehicle Identifiers: Plate + Marca / Modelo -->
            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <!-- Plate Badge -->
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted copy-icon"
                  />
                </div>

                <!-- Marca & Modelo -->
                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || assetPlate || assetChassis || assetId }}
                  </h5>
                </div>

                <!-- Nickname Tag if any -->
                <b-badge
                  v-if="assetData && assetData.nickname"
                  variant="light-primary"
                  class="ml-50 badge-glow font-weight-normal"
                >
                  {{ assetData.nickname }}
                </b-badge>
              </div>

              <!-- Subtitle with Chassis if no plate -->
              <div
                v-if="!assetPlate && assetChassis"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis, $t('chassis'))"
                  >
                    {{ assetChassis }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <!-- Right: Chassis Chip, Group, Customer & Action Button -->
          <div
            class="d-flex align-items-center flex-wrap"
            style="gap: 6px;"
          >
            <!-- Chassis Chip (only shown when plate is already shown on the left) -->
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer mr-50 mb-50 mb-md-0"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
              <feather-icon
                icon="CopyIcon"
                size="12"
                class="ml-50 text-muted copy-icon"
              />
            </div>

            <!-- Group Chip if any -->
            <div
              v-if="assetGroupName"
              class="meta-chip mr-50 mb-50 mb-md-0"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <!-- Customer Chip if any -->
            <div
              v-if="assetCustomerName"
              class="meta-chip mr-50 mb-50 mb-md-0"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>

            <!-- Action Button to Asset Summary -->
            <div class="asset-actions flex-shrink-0">
              <b-button
                v-b-tooltip.hover
                variant="outline-primary"
                size="sm"
                class="btn-view-details d-inline-flex align-items-center"
                :title="$t('goToAssetSummary')"
                @click="goToAssetSummary"
              >
                <feather-icon
                  icon="ActivityIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('assetSummary') }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Empty State / Asset Selector Prompt if no asset in URL -->
    <b-card
      v-if="!assetId"
      class="shadow-sm border-0 mb-2 p-2 text-center"
    >
      <b-avatar
        variant="light-primary"
        size="70px"
        class="mb-1 shadow-xs"
      >
        <feather-icon
          icon="TruckIcon"
          size="36"
          class="text-primary"
        />
      </b-avatar>
      <h4 class="font-weight-bolder mb-50">
        {{ $t('Select a Vehicle for Payload Analysis') }}
      </h4>
      <p
        class="text-muted font-small-3 mb-2 mx-auto"
        style="max-width: 540px;"
      >
        {{ $t('To generate telemetry, load compliance and operational reports, select a vehicle below or access from the fleet list.') }}
      </p>

      <div
        class="mx-auto"
        style="max-width: 440px;"
      >
        <v-select
          v-model="selectedAssetItem"
          :options="assetOptions"
          label="label"
          :placeholder="$t('Search by model or chassis...')"
          :filterable="false"
          class="mb-1 text-left"
          @search="onAssetSearch"
          @input="onAssetSelect"
        >
          <template #option="option">
            <div class="d-flex align-items-center justify-content-between py-25">
              <div>
                <strong>{{ option.modelName || option.chassisNumber || option.id }}</strong>
                <small class="text-muted d-block">{{ option.manufacturerName || option.chassisNumber }}</small>
              </div>
              <b-badge
                v-if="option.assetGroupName"
                variant="light-secondary"
                pill
              >
                {{ option.assetGroupName }}
              </b-badge>
            </div>
          </template>
        </v-select>

        <b-button
          variant="outline-primary"
          size="sm"
          :to="{ name: 'apps-asset-list' }"
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="ListIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ $t('See Full List of Assets') }}</span>
        </b-button>
      </div>
    </b-card>

    <template v-else>
      <!-- Telemetry Estimated Payload Notice Banner -->
      <b-alert
        v-model="showEstimatedAlert"
        variant="light-info"
        dismissible
        class="mb-1 border-info shadow-xs"
      >
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between py-25">
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-info"
              size="38px"
              class="mr-1 shadow-xs flex-shrink-0"
            >
              <feather-icon
                icon="InfoIcon"
                size="20"
                class="text-info"
              />
            </b-avatar>
            <div>
              <div
                class="d-flex align-items-center flex-wrap"
                style="gap: 6px;"
              >
                <h6 class="mb-0 font-weight-bolder text-info">
                  {{ $t('Measurement Methodology: Estimated Load (Median of Stable Samples)') }}
                </h6>
                <b-badge
                  variant="info"
                  pill
                  class="font-small-1"
                >
                  {{ $t('Estimated Load') }}
                </b-badge>
              </div>
              <p class="mb-0 font-small-2 text-muted mt-25">
                {{ $t('The trip weight reflects the statistical median of the stable load on the route. Pothole spikes, sudden braking, moving scales, rising loading and lowering unloading are left out.') }}
              </p>
            </div>
          </div>

          <div class="mt-75 mt-md-0 ml-md-1 flex-shrink-0">
            <b-button
              variant="outline-info"
              size="sm"
              class="d-inline-flex align-items-center"
              @click="openGuideModal(0)"
            >
              <feather-icon
                icon="BookOpenIcon"
                size="13"
                class="mr-50"
              />
              <span>{{ $t('Report Guide') }}</span>
            </b-button>
          </div>
        </div>
      </b-alert>

      <!-- Advanced Filter Card -->
      <transition name="slide-fade">
        <b-card
          v-if="showFilters"
          no-body
          class="mb-1 shadow-sm border-0 filters-card"
        >
          <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="SlidersIcon"
                size="16"
                class="text-primary mr-50"
              />
              <h6 class="mb-0 font-weight-bolder">
                {{ $t('Payload Report Filters') }}
              </h6>
              <b-badge
                v-if="activeFiltersCount > 0"
                variant="light-primary"
                pill
                class="ml-50"
              >
                {{ $t('{count} active', { count: activeFiltersCount }) }}
              </b-badge>
            </div>

            <div
              class="d-flex align-items-center"
              style="gap: 6px;"
            >
              <b-button
                v-if="activeFiltersCount > 0"
                v-b-tooltip.hover
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                :title="$t('Clear Filters')"
                @click="clearFilters"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="14"
                />
              </b-button>
              <b-button
                v-b-tooltip.hover
                variant="flat-secondary"
                size="sm"
                class="btn-icon"
                :title="$t('Close Filters')"
                @click="showFilters = false"
              >
                <feather-icon
                  icon="XIcon"
                  size="16"
                />
              </b-button>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <!-- Period Shortcuts Chips -->
            <div
              class="mb-1 d-flex flex-wrap align-items-center"
              style="gap: 6px;"
            >
              <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
              <b-badge
                href="javascript:void(0)"
                :variant="dateRangePreset === 'today' ? 'primary' : 'light-secondary'"
                pill
                class="cursor-pointer py-50 px-75"
                @click="setDatePreset('today')"
              >
                {{ $t('Today') }}
              </b-badge>
              <b-badge
                href="javascript:void(0)"
                :variant="dateRangePreset === 'yesterday' ? 'primary' : 'light-secondary'"
                pill
                class="cursor-pointer py-50 px-75"
                @click="setDatePreset('yesterday')"
              >
                {{ $t('Yesterday') }}
              </b-badge>
              <b-badge
                href="javascript:void(0)"
                :variant="dateRangePreset === 'last7' ? 'primary' : 'light-secondary'"
                pill
                class="cursor-pointer py-50 px-75"
                @click="setDatePreset('last7')"
              >
                {{ $t('Last 7 days') }}
              </b-badge>
              <b-badge
                href="javascript:void(0)"
                :variant="dateRangePreset === 'month' ? 'primary' : 'light-secondary'"
                pill
                class="cursor-pointer py-50 px-75"
                @click="setDatePreset('month')"
              >
                {{ $t('This Month') }}
              </b-badge>
            </div>

            <b-row>
              <!-- Data Inicial -->
              <b-col
                cols="12"
                sm="6"
                lg="3"
                class="mb-1"
              >
                <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                  <feather-icon
                    icon="CalendarIcon"
                    size="13"
                    class="mr-25 text-primary"
                  />
                  <span>{{ $t('Start Date') }}</span>
                  <span class="text-danger ml-25">*</span>
                </label>
                <b-input-group size="sm">
                  <b-input-group-prepend is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                    />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="dateStart"
                    class="form-control"
                    :config="flatPickrConfig"
                    :placeholder="$t('Select...')"
                    @on-change="onCustomDateChange"
                  />
                </b-input-group>
              </b-col>

              <!-- Data Final -->
              <b-col
                cols="12"
                sm="6"
                lg="3"
                class="mb-1"
              >
                <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                  <feather-icon
                    icon="CalendarIcon"
                    size="13"
                    class="mr-25 text-primary"
                  />
                  <span>{{ $t('End Date') }}</span>
                  <span class="text-danger ml-25">*</span>
                </label>
                <b-input-group size="sm">
                  <b-input-group-prepend is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                    />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="dateEnd"
                    class="form-control"
                    :config="flatPickrConfig"
                    :placeholder="$t('Select...')"
                    @on-change="onCustomDateChange"
                  />
                </b-input-group>
              </b-col>

              <!-- Capacidade Nominal (toneladas) -->
              <b-col
                cols="12"
                sm="6"
                lg="3"
                class="mb-1"
              >
                <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                  <feather-icon
                    icon="TargetIcon"
                    size="13"
                    class="mr-25 text-primary"
                  />
                  <span>{{ $t('Nominal Capacity') }}</span>
                  <feather-icon
                    v-b-tooltip.hover
                    icon="HelpCircleIcon"
                    size="12"
                    class="ml-25 text-muted cursor-pointer"
                    :title="$t('Nominal vehicle capacity in tons (> 0). Without it there is no band classification, distribution or histogram.')"
                  />
                </label>
                <b-input-group size="sm">
                  <b-form-input
                    ref="nominalCapacityInput"
                    v-model="nominalCapacity"
                    type="number"
                    step="0.1"
                    min="0.1"
                    :placeholder="$t('E.g.: 60')"
                  />
                  <b-input-group-append is-text>
                    <small class="font-weight-bold">t</small>
                  </b-input-group-append>
                </b-input-group>
              </b-col>

            </b-row>

            <!-- Filter Actions Bar -->
            <div class="d-flex align-items-center justify-content-between flex-wrap mt-1 pt-75 border-top">
              <div class="text-muted font-small-2 d-flex align-items-center">
                <feather-icon
                  icon="LockIcon"
                  size="14"
                  class="mr-50 text-primary"
                />
                <span>
                  {{ $t('Vehicle:') }} <strong class="text-primary">{{ assetModel || assetChassis || assetId }}</strong>. <span class="text-muted">{{ $t('The server handles times in the user\'s local zone.') }}</span>
                </span>
              </div>
              <div
                class="d-flex align-items-center"
                style="gap: 8px;"
              >
                <b-button
                  v-if="activeFiltersCount > 0"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearFilters"
                >
                  {{ $t('Restore Default') }}
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  class="d-inline-flex align-items-center"
                  @click="fetchData"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span>{{ $t('Apply Filters') }}</span>
                </b-button>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </transition>

      <!-- Active Filters Pill Bar (When filters card is hidden) -->
      <div
        v-if="!showFilters"
        class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
        style="gap: 6px;"
      >
        <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
        <b-badge
          variant="light-primary"
          pill
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="LockIcon"
            size="12"
            class="mr-25"
          />
          {{ assetModel || assetChassis || assetId }}
        </b-badge>
        <b-badge
          v-if="dateStart || dateEnd"
          variant="light-primary"
          pill
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="CalendarIcon"
            size="12"
            class="mr-25"
          />
          {{ formatDateDisplay(dateStart) }} — {{ formatDateDisplay(dateEnd) }}
        </b-badge>
        <b-badge
          v-if="hasNominalCapacity"
          variant="light-warning"
          pill
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="TargetIcon"
            size="12"
            class="mr-25"
          />
          {{ $t('Capacity:') }} {{ nominalCapacity }} t
          <feather-icon
            icon="XIcon"
            size="10"
            class="ml-25 cursor-pointer"
            @click="clearNominalCapacity"
          />
        </b-badge>
        <b-link
          class="font-small-2 font-weight-bold ml-50 text-danger"
          @click="clearFilters"
        >
          {{ $t('Clear') }}
        </b-link>
      </div>

      <!-- SECTION 1: 6 EXECUTIVE KPI CARDS -->
      <b-row class="match-height mb-1">
        <!-- KPI 1: Carga Total Transportada -->
        <b-col
          cols="6"
          sm="6"
          md="4"
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="isLoadingSummary">
              <b-skeleton
                width="60%"
                height="14px"
                class="mb-50"
              />
              <b-skeleton
                width="80%"
                height="28px"
                class="mb-25"
              />
              <b-skeleton
                width="50%"
                height="12px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-25">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                      {{ $t('Total Load') }}
                    </span>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      :title="$t('Total transported: sum of the loads of the cycles considered. Pothole spikes, braking and moving swings are left out.')"
                      @click="openGuideModal(2)"
                    />
                  </div>
                  <h3 class="font-weight-bolder text-primary mb-0 mt-25">
                    {{ formatNumber(kpis.totalTransportedTonnes) }} <small class="kpi-unit">t</small>
                  </h3>
                </div>
                <b-avatar
                  variant="light-primary"
                  size="42px"
                  class="shadow-xs flex-shrink-0 cursor-pointer"
                  @click="openGuideModal(2)"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="20"
                    class="text-primary"
                  />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top d-flex justify-content-between align-items-center">
                <span>{{ $t('Sum of estimated payload') }}</span>
                <feather-icon
                  v-b-tooltip.hover
                  icon="InfoIcon"
                  size="12"
                  class="text-muted cursor-pointer"
                  :title="$t('See methodology and indicators')"
                  @click="openGuideModal(2)"
                />
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- KPI 2: Ciclos Considerados -->
        <b-col
          cols="6"
          sm="6"
          md="4"
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="isLoadingSummary">
              <b-skeleton
                width="60%"
                height="14px"
                class="mb-50"
              />
              <b-skeleton
                width="80%"
                height="28px"
                class="mb-25"
              />
              <b-skeleton
                width="50%"
                height="12px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-25">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                      {{ $t('Valid Cycles') }}
                    </span>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      :title="$t('Cycles: number of complete trips in the period (load, continue with the load and unload). Jolts or short maneuvers do not turn into a cycle.')"
                      @click="openGuideModal(0)"
                    />
                  </div>
                  <h3 class="font-weight-bolder text-info mb-0 mt-25">
                    {{ formatInteger(kpis.cycleCount) }}
                  </h3>
                </div>
                <b-avatar
                  variant="light-info"
                  size="42px"
                  class="shadow-xs flex-shrink-0 cursor-pointer"
                  @click="openGuideModal(0)"
                >
                  <feather-icon
                    icon="RepeatIcon"
                    size="20"
                    class="text-info"
                  />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top d-flex justify-content-between align-items-center">
                <span>{{ $t('All cycles') }}</span>
                <feather-icon
                  v-b-tooltip.hover
                  icon="InfoIcon"
                  size="12"
                  class="text-muted cursor-pointer"
                  :title="$t('See methodology and indicators')"
                  @click="openGuideModal(0)"
                />
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- KPI 3: Carga Média & Mediana -->
        <b-col
          cols="6"
          sm="6"
          md="4"
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="isLoadingSummary">
              <b-skeleton
                width="60%"
                height="14px"
                class="mb-50"
              />
              <b-skeleton
                width="80%"
                height="28px"
                class="mb-25"
              />
              <b-skeleton
                width="50%"
                height="12px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-25">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                      {{ $t('Mean / Median') }}
                    </span>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      :title="$t('Average adds the loads and divides by the cycles (pulled by extreme trips). Median is the middle load and shows the typical trip. Next = consistency; different = atypical trips.')"
                      @click="openGuideModal(1)"
                    />
                  </div>
                  <h3 class="font-weight-bolder text-success mb-0 mt-25">
                    {{ formatNumber(kpis.averagePayloadTonnes) }} <small class="kpi-unit">t</small>
                  </h3>
                </div>
                <b-avatar
                  variant="light-success"
                  size="42px"
                  class="shadow-xs flex-shrink-0 cursor-pointer"
                  @click="openGuideModal(1)"
                >
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="20"
                    class="text-success"
                  />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top d-flex justify-content-between align-items-center">
                <span
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Median: central load with cycles in order (shows the typical trip)')"
                  @click="openGuideModal(1)"
                >
                  {{ $t('Median:') }} <strong>{{ formatNumber(kpis.medianPayloadTonnes) }}t</strong>
                </span>
                <span
                  v-if="kpis.variabilityPercent !== null"
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Variability: how much the loads deviate from the average. Low = similar; high = irregular.')"
                  @click="openGuideModal(1)"
                >
                  {{ $t('Var:') }} <strong>{{ formatNumber(kpis.variabilityPercent) }}%</strong>
                </span>
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- KPI 4: Taxa de Conformidade -->
        <b-col
          cols="6"
          sm="6"
          md="4"
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="isLoadingSummary">
              <b-skeleton
                width="60%"
                height="14px"
                class="mb-50"
              />
              <b-skeleton
                width="80%"
                height="28px"
                class="mb-25"
              />
              <b-skeleton
                width="50%"
                height="12px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-25">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                      {{ $t('Compliance') }}
                    </span>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      :title="$t('Compliance: percentage of cycles in the ideal range (90% to 110% of capacity). Loss: tons missing from the nominal capacity (a heavy trip does not compensate for a light one). Excess: weight above the ceiling of 110%.')"
                      @click="openGuideModal(2)"
                    />
                  </div>
                  <h3
                    v-if="kpis.complianceRate !== null"
                    class="font-weight-bolder mb-0 mt-25"
                    :class="getComplianceColorClass(kpis.complianceRate)"
                  >
                    {{ formatNumber(kpis.complianceRate) }}%
                  </h3>
                  <div
                    v-else
                    class="mt-25"
                  >
                    <b-badge
                      variant="light-secondary"
                      class="font-small-1"
                    >
                      {{ $t('No Capacity') }}
                    </b-badge>
                  </div>
                </div>
                <b-avatar
                  :variant="kpis.complianceRate !== null ? 'light-warning' : 'light-secondary'"
                  size="42px"
                  class="shadow-xs flex-shrink-0 cursor-pointer"
                  @click="openGuideModal(2)"
                >
                  <feather-icon
                    icon="AwardIcon"
                    size="20"
                    :class="kpis.complianceRate !== null ? 'text-warning' : 'text-muted'"
                  />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top d-flex justify-content-between align-items-center">
                <span
                  v-if="kpis.capacityLossTonnes !== null"
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Loss of capacity: missing tons to reach nominal capacity (one trip above does not compensate for another trip below)')"
                  @click="openGuideModal(2)"
                >
                  {{ $t('Loss:') }} {{ formatNumber(kpis.capacityLossTonnes) }}t
                </span>
                <span
                  v-if="kpis.excessAboveIdealTonnes !== null"
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Excess above ideal: tons added beyond the ceiling of 110% of nominal capacity')"
                  @click="openGuideModal(2)"
                >
                  {{ $t('Exc:') }} {{ formatNumber(kpis.excessAboveIdealTonnes) }}t
                </span>
                <b-link
                  v-if="kpis.complianceRate === null"
                  class="text-primary font-weight-bold"
                  @click="focusNominalCapacity"
                >
                  {{ $t('Enter Capacity') }}
                </b-link>
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- KPI 5: Produtividade Horária -->
        <b-col
          cols="6"
          sm="6"
          md="4"
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="isLoadingSummary">
              <b-skeleton
                width="60%"
                height="14px"
                class="mb-50"
              />
              <b-skeleton
                width="80%"
                height="28px"
                class="mb-25"
              />
              <b-skeleton
                width="50%"
                height="12px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-25">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                      {{ $t('Productivity') }}
                    </span>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      :title="$t('Effective Productivity: t/h that the equipment was working (without long stops). Clock Productivity: t/h from the 1st to the last trip with all waiting times included.')"
                      @click="openGuideModal(2)"
                    />
                  </div>
                  <h3 class="font-weight-bolder text-dark mb-0 mt-25">
                    {{ formatNumber(kpis.effectiveProductivityTonnesPerHour || kpis.clockProductivityTonnesPerHour) }}
                    <small class="kpi-unit">t/h</small>
                  </h3>
                </div>
                <b-avatar
                  variant="light-secondary"
                  size="42px"
                  class="shadow-xs flex-shrink-0 cursor-pointer"
                  @click="openGuideModal(2)"
                >
                  <feather-icon
                    icon="ZapIcon"
                    size="20"
                    class="text-primary"
                  />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top d-flex justify-content-between align-items-center">
                <span
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Effective productivity: tons per hour worked (long stops are excluded)')"
                  @click="openGuideModal(2)"
                >
                  {{ $t('Effective:') }} {{ formatNumber(kpis.effectiveProductivityTonnesPerHour) }} t/h
                </span>
                <span
                  v-if="kpis.clockProductivityTonnesPerHour !== null"
                  v-b-tooltip.hover
                  class="cursor-pointer font-small-1 text-muted ml-50"
                  :title="$t('Clock productivity: from the 1st to the last trip, including waiting')"
                  @click="openGuideModal(2)"
                >
                  {{ $t('Clock:') }} {{ formatNumber(kpis.clockProductivityTonnesPerHour) }} t/h
                </span>
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- KPI 6: Ritmo de Ciclo Médio -->
        <b-col
          cols="6"
          sm="6"
          md="4"
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="isLoadingSummary">
              <b-skeleton
                width="60%"
                height="14px"
                class="mb-50"
              />
              <b-skeleton
                width="80%"
                height="28px"
                class="mb-25"
              />
              <b-skeleton
                width="50%"
                height="12px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-25">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                      {{ $t('Average Cycle') }}
                    </span>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      :title="$t('Average duration: from the beginning of charging to the end of discharging. Average empty return: from the end of one trip to the beginning of the next. Average interval: from the beginning of one trip to the beginning of the next (trip + waiting).')"
                      @click="openGuideModal(2)"
                    />
                  </div>
                  <h3 class="font-weight-bolder text-dark mb-0 mt-25">
                    {{ formatNumber(kpis.averageCycleMinutes) }} <small class="kpi-unit">min</small>
                  </h3>
                </div>
                <b-avatar
                  variant="light-secondary"
                  size="42px"
                  class="shadow-xs flex-shrink-0 cursor-pointer"
                  @click="openGuideModal(2)"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="20"
                    class="text-primary"
                  />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top d-flex justify-content-between align-items-center">
                <span
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Average empty return: time between the end of one trip and the start of the next')"
                  @click="openGuideModal(2)"
                >
                  {{ $t('Empty:') }} {{ formatNumber(kpis.averageEmptyReturnMinutes) }}m
                </span>
                <span
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('Average interval: time between the start of one trip and the start of the next (with the trip and waiting)')"
                  @click="openGuideModal(2)"
                >
                  {{ $t('Interv:') }} {{ formatNumber(kpis.averageIntervalMinutes) }}m
                </span>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <!-- SECTION 2: ANALYTICAL TABS (DISTRIBUIÇÃO, PRAÇAS MAPA, GRÁFICO CICLOS, QUALIDADE & ALERTAS) -->
      <b-card
        no-body
        class="shadow-sm border-0 mb-2"
      >
        <b-tabs
          v-model="activeTab"
          pills
          class="p-1 pb-0 border-bottom"
          nav-class="mb-0"
        >
          <b-tab title-item-class="mr-50">
            <template #title>
              <feather-icon
                icon="PieChartIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('Distribution & Histogram') }}</span>
            </template>
          </b-tab>

          <b-tab title-item-class="mr-50">
            <template #title>
              <feather-icon
                icon="MapPinIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('Operational Yards (Map)') }}</span>
              <b-badge
                v-if="zonesTotalCount > 0"
                variant="light-primary"
                pill
                class="ml-50"
              >
                {{ zonesTotalCount }}
              </b-badge>
            </template>
          </b-tab>

          <b-tab title-item-class="mr-50">
            <template #title>
              <feather-icon
                icon="BarChart2Icon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('Chronological Evolution of Cycles') }}</span>
            </template>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="ShieldCheckIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('Quality & Alerts') }}</span>
              <b-badge
                v-if="alertsList.length > 0"
                variant="light-warning"
                pill
                class="ml-50"
              >
                {{ alertsList.length }}
              </b-badge>
            </template>
          </b-tab>
        </b-tabs>

        <!-- TAB CONTENT CONTAINER -->
        <div class="p-1">
          <!-- TAB 0: DISTRIBUIÇÃO & HISTOGRAMA -->
          <div v-if="activeTab === 0">
            <!-- If classification available (nominalCapacity defined) -->
            <template v-if="hasNominalCapacity && distribution">
              <b-row class="align-items-center mb-2">
                <!-- Left: 4 Range Distribution Pill Cards -->
                <b-col
                  cols="12"
                  md="5"
                  lg="4"
                >
                  <!-- Subcarga (<90%) -->
                  <div class="distribution-pill-box p-75 mb-75 rounded border border-warning bg-light-warning">
                    <div class="d-flex justify-content-between align-items-center mb-25">
                      <span class="font-small-2 font-weight-bolder text-warning">
                        <feather-icon
                          icon="ArrowDownIcon"
                          size="12"
                          class="mr-25"
                        />
                        {{ $t('Underload (\u003c 90%)') }}
                      </span>
                      <strong class="font-small-2 text-warning">
                        {{ distribution.underload ? distribution.underload.percentage : 0 }}%
                      </strong>
                    </div>
                    <div class="d-flex align-items-baseline justify-content-between">
                      <h5 class="mb-0 font-weight-bolder text-warning">
                        {{ distribution.underload ? distribution.underload.cycles : 0 }}
                        <small class="font-small-1">{{ $t('cycles') }}</small>
                      </h5>
                      <small class="text-muted font-small-1">
                        &lt; {{ formatNumber(Number(nominalCapacity) * 0.9) }} t
                      </small>
                    </div>
                  </div>

                  <!-- Conforme (90% - 110%) -->
                  <div class="distribution-pill-box p-75 mb-75 rounded border border-success bg-light-success">
                    <div class="d-flex justify-content-between align-items-center mb-25">
                      <span class="font-small-2 font-weight-bolder text-success">
                        <feather-icon
                          icon="CheckIcon"
                          size="12"
                          class="mr-25"
                        />
                        {{ $t('Compliant / Ideal (90% to 110%)') }}
                      </span>
                      <strong class="font-small-2 text-success">
                        {{ distribution.optimal ? distribution.optimal.percentage : 0 }}%
                      </strong>
                    </div>
                    <div class="d-flex align-items-baseline justify-content-between">
                      <h5 class="mb-0 font-weight-bolder text-success">
                        {{ distribution.optimal ? distribution.optimal.cycles : 0 }}
                        <small class="font-small-1">{{ $t('cycles') }}</small>
                      </h5>
                      <small class="text-muted font-small-1">
                        {{ formatNumber(Number(nominalCapacity) * 0.9) }} - {{ formatNumber(Number(nominalCapacity) * 1.1) }} t
                      </small>
                    </div>
                  </div>

                  <!-- Sobrecarga (110% - 120%) -->
                  <div class="distribution-pill-box p-75 mb-75 rounded border border-danger bg-light-danger">
                    <div class="d-flex justify-content-between align-items-center mb-25">
                      <span class="font-small-2 font-weight-bolder text-danger">
                        <feather-icon
                          icon="AlertTriangleIcon"
                          size="12"
                          class="mr-25"
                        />
                        {{ $t('Overload (110% to 120%)') }}
                      </span>
                      <strong class="font-small-2 text-danger">
                        {{ distribution.overload ? distribution.overload.percentage : 0 }}%
                      </strong>
                    </div>
                    <div class="d-flex align-items-baseline justify-content-between">
                      <h5 class="mb-0 font-weight-bolder text-danger">
                        {{ distribution.overload ? distribution.overload.cycles : 0 }}
                        <small class="font-small-1">{{ $t('cycles') }}</small>
                      </h5>
                      <small class="text-muted font-small-1">
                        {{ formatNumber(Number(nominalCapacity) * 1.1) }} - {{ formatNumber(Number(nominalCapacity) * 1.2) }} t
                      </small>
                    </div>
                  </div>

                  <!-- Sobrecarga Severa (> 120%) -->
                  <div class="distribution-pill-box severe-overload p-75 rounded border">
                    <div class="d-flex justify-content-between align-items-center mb-25">
                      <span class="font-small-2 font-weight-bolder text-severe">
                        <feather-icon
                          icon="AlertOctagonIcon"
                          size="12"
                          class="mr-25"
                        />
                        {{ $t('Severe Overload (> 120%)') }}
                      </span>
                      <strong class="font-small-2 text-severe">
                        {{ distribution.severeOverload ? distribution.severeOverload.percentage : 0 }}%
                      </strong>
                    </div>
                    <div class="d-flex align-items-baseline justify-content-between">
                      <h5 class="mb-0 font-weight-bolder text-severe">
                        {{ distribution.severeOverload ? distribution.severeOverload.cycles : 0 }}
                        <small class="font-small-1">{{ $t('cycles') }}</small>
                      </h5>
                      <small class="text-muted font-small-1">
                        &gt; {{ formatNumber(Number(nominalCapacity) * 1.2) }} t
                      </small>
                    </div>
                  </div>

                  <!-- Criteria Info Box -->
                  <div class="mt-75 p-75 rounded bg-light-secondary border font-small-2 shadow-xs">
                    <div class="font-weight-bolder text-dark mb-50 d-flex align-items-center">
                      <feather-icon
                        icon="TargetIcon"
                        size="13"
                        class="mr-25 text-primary"
                      />
                      <span>{{ $t('Nominal Classification Criteria') }}</span>
                    </div>
                    <div
                      class="d-flex flex-column font-small-1"
                      style="gap: 4px;"
                    >
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="text-warning font-weight-bold">{{ $t('● Underload') }}</span>
                        <b-badge
                          variant="light-warning"
                          pill
                        >
                          {{ $t('\u003c 90% of Capacity') }}
                        </b-badge>
                      </div>
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="text-success font-weight-bold">{{ $t('● Compliant') }}</span>
                        <b-badge
                          variant="light-success"
                          pill
                        >
                          {{ $t('90% to 110% of Capacity') }}
                        </b-badge>
                      </div>
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="text-danger font-weight-bold">{{ $t('● Overload') }}</span>
                        <b-badge
                          variant="light-danger"
                          pill
                        >
                          {{ $t('110% to 120% of Capacity') }}
                        </b-badge>
                      </div>
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="text-severe font-weight-bold">{{ $t('● Severe Overload') }}</span>
                        <b-badge
                          class="badge-severe-overload"
                          pill
                        >
                          {{ $t('> 120% of Capacity') }}
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </b-col>

                <!-- Right: Donut Chart -->
                <b-col
                  cols="12"
                  md="7"
                  lg="8"
                  class="mt-1 mt-md-0 d-flex justify-content-center"
                >
                  <div style="width: 100%; max-width: 520px;">
                    <vue-apex-charts
                      type="donut"
                      height="330"
                      :options="donutChartOptions"
                      :series="donutChartSeries"
                    />
                  </div>
                </b-col>
              </b-row>

              <!-- Histogram of Cycles (Bar Chart) -->
              <div class="pt-1 border-top">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <div>
                    <h6 class="mb-25 font-weight-bolder text-primary d-flex align-items-center">
                      <feather-icon
                        icon="BarChart2Icon"
                        size="16"
                        class="mr-50"
                      />
                      {{ $t('Cycle Histogram by Weight Ranges') }}
                    </h6>
                    <small class="text-muted">
                      {{ $t('Quantitative distribution of cycles grouped according to the 4 load compliance bands') }}
                    </small>
                  </div>
                </div>

                <template v-if="histogramData && histogramData.length > 0">
                  <vue-apex-charts
                    type="bar"
                    height="260"
                    :options="histogramChartOptions"
                    :series="histogramChartSeries"
                  />
                </template>
                <template v-else>
                  <div class="text-center py-3 text-muted">
                    <feather-icon
                      icon="BarChartIcon"
                      size="32"
                      class="mb-50 text-muted"
                    />
                    <p class="mb-0 font-small-3">
                      {{ $t('No histogram data returned for the period.') }}
                    </p>
                  </div>
                </template>
              </div>
            </template>

            <!-- Motivational Callout if no nominal capacity informed -->
            <div
              v-else
              class="text-center py-4 border rounded bg-light-warning"
            >
              <b-avatar
                variant="light-warning"
                size="56px"
                class="mb-1"
              >
                <feather-icon
                  icon="TargetIcon"
                  size="28"
                  class="text-warning"
                />
              </b-avatar>
              <h5 class="font-weight-bolder text-warning mb-50">
                {{ $t('Enter the Nominal Capacity to Enable Classification') }}
              </h5>
              <p
                class="text-muted font-small-3 mb-1 mx-auto"
                style="max-width: 580px;"
              >
                {{ $t('Without the truck capacity informed, the report shows the load in tons and omits compliance, loss, excess and the classification into bands (Underload, Compliance and Overload).') }}
              </p>
              <div
                class="d-flex align-items-center justify-content-center flex-wrap"
                style="gap: 8px;"
              >
                <b-button
                  variant="warning"
                  size="sm"
                  class="d-inline-flex align-items-center"
                  @click="focusNominalCapacity"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span>{{ $t('Configure Nominal Capacity (t)') }}</span>
                </b-button>
                <b-button
                  variant="outline-warning"
                  size="sm"
                  class="d-inline-flex align-items-center"
                  @click="openGuideModal(2)"
                >
                  <feather-icon
                    icon="HelpCircleIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span>{{ $t('Understand the Ranges and Indicators') }}</span>
                </b-button>
              </div>
            </div>
          </div>

          <!-- TAB 1: PRAÇAS OPERACIONAIS (MAPA) -->
          <div v-else-if="activeTab === 1">
            <payload-zones-map
              :load-zones="loadZones"
              :unload-zones="unloadZones"
              :nominal-capacity="nominalCapacity"
              @open-guide="openGuideModal"
            />
          </div>

          <!-- TAB 2: EVOLUÇÃO CRONOLÓGICA DOS CICLOS (SCATTER / LINE) -->
          <div v-else-if="activeTab === 2">
            <div class="d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom">
              <div>
                <h6 class="mb-25 font-weight-bolder text-primary d-flex align-items-center">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="16"
                    class="mr-50"
                  />
                  {{ $t('Load-to-Load Evolution of Cycles') }}
                </h6>
                <small class="text-muted">
                  {{ $t('Estimated weighing of each cycle in order of execution, with nominal capacity limits') }}
                </small>
              </div>
              <div
                v-if="hasNominalCapacity"
                class="font-small-1 text-muted"
              >
                {{ $t('Nominal Capacity:') }} <strong class="text-primary">{{ nominalCapacity }} t</strong>
              </div>
            </div>

            <template v-if="allCyclesData && allCyclesData.length > 0">
              <!-- Controls & Indicators Bar for Many Cycles (UI & UX improvements) -->
              <div
                class="cycles-timeline-controls mb-1 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center"
                style="gap: 8px;"
              >
                <!-- Left: Indicators & scroll notice -->
                <div
                  class="d-flex align-items-center flex-wrap"
                  style="gap: 8px;"
                >
                  <b-badge
                    variant="light-primary"
                    pill
                    class="d-inline-flex align-items-center py-25 px-50 font-small-1"
                  >
                    <feather-icon
                      icon="TrendingUpIcon"
                      size="12"
                      class="mr-25"
                    />
                    {{ $t('{count} cycles in the period', { count: allCyclesData.length }) }}
                  </b-badge>

                  <!-- Horizontal scroll notice when many points -->
                  <b-badge
                    v-if="allCyclesData.length > 16 && !isFitToScreen"
                    variant="light-info"
                    pill
                    class="d-inline-flex align-items-center py-25 px-50 font-small-1"
                  >
                    <feather-icon
                      icon="MoveIcon"
                      size="12"
                      class="mr-25"
                    />
                    {{ $t('swipeHorizontallyToNavigate') }} {{ allCyclesData.length }} {{ $t('cycles') }}
                  </b-badge>

                  <small class="text-muted d-none d-md-inline font-small-1">
                    {{ $t('• Click on a cycle for details') }}
                  </small>
                </div>

                <!-- Right: Quick Navigation & View Mode Toggles -->
                <div
                  v-if="allCyclesData.length > 16"
                  class="d-flex align-items-center flex-wrap"
                  style="gap: 8px;"
                >
                  <!-- Scroll to Start / End buttons in scroll mode -->
                  <div
                    v-if="!isFitToScreen"
                    class="btn-group btn-group-sm"
                  >
                    <b-button
                      v-b-tooltip.hover
                      variant="outline-secondary"
                      size="sm"
                      class="py-25 px-50 font-small-1 d-inline-flex align-items-center"
                      :title="$t('Go to the first cycle (start)')"
                      @click="scrollToStart"
                    >
                      <feather-icon
                        icon="ChevronsLeftIcon"
                        size="12"
                        class="mr-25"
                      />
                      {{ $t('Start') }}
                    </b-button>
                    <b-button
                      v-b-tooltip.hover
                      variant="outline-secondary"
                      size="sm"
                      class="py-25 px-50 font-small-1 d-inline-flex align-items-center"
                      :title="$t('Go to the most recent cycle (end)')"
                      @click="scrollToEnd"
                    >
                      {{ $t('End') }}
                      <feather-icon
                        icon="ChevronsRightIcon"
                        size="12"
                        class="ml-25"
                      />
                    </b-button>
                  </div>

                  <!-- Toggle between Scroll (Expanded) and Fit to Screen -->
                  <div class="btn-group btn-group-sm">
                    <b-button
                      v-b-tooltip.hover
                      :variant="!isFitToScreen ? 'secondary' : 'outline-secondary'"
                      size="sm"
                      class="py-25 px-50 font-small-1 d-inline-flex align-items-center"
                      :title="$t('Expanded view with horizontal scrolling: ideal for analyzing cycle by cycle')"
                      @click="isFitToScreen = false"
                    >
                      <feather-icon
                        icon="Maximize2Icon"
                        size="12"
                        class="mr-25"
                      />
                      {{ $t('Scrolling') }}
                    </b-button>
                    <b-button
                      v-b-tooltip.hover
                      :variant="isFitToScreen ? 'secondary' : 'outline-secondary'"
                      size="sm"
                      class="py-25 px-50 font-small-1 d-inline-flex align-items-center"
                      :title="$t('Fit All Cycles to Screen Width: Compact Overview')"
                      @click="isFitToScreen = true"
                    >
                      {{ $t('Adjust') }}
                      <feather-icon
                        icon="Minimize2Icon"
                        size="12"
                        class="ml-25"
                      />
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Horizontal Scrollable Container for Cycles Line Chart -->
              <div
                ref="cyclesTimelineScrollContainer"
                class="cycles-timeline-scroll-container"
              >
                <div
                  class="cycles-timeline-chart-wrapper"
                  :style="{ minWidth: cyclesTimelineChartMinWidth }"
                >
                  <vue-apex-charts
                    :key="`cycles-timeline-${isFitToScreen ? 'fit' : 'scroll'}-${allCyclesData.length}`"
                    type="line"
                    height="340"
                    :options="cyclesTimelineChartOptions"
                    :series="cyclesTimelineChartSeries"
                  />
                </div>
              </div>

              <!-- Legenda dos limites abaixo do gráfico -->
              <div
                v-if="hasNominalCapacity"
                class="chart-limits-legend d-flex flex-wrap justify-content-center align-items-center mt-1 pt-75 border-top"
                style="gap: 12px;"
              >
                <div class="legend-item d-inline-flex align-items-center font-small-2">
                  <span class="legend-line-indicator legend-overload mr-50" />
                  <span class="text-muted mr-25">{{ $t('Conforming Limit (110%):') }}</span>
                  <strong class="text-danger">{{ (Number(nominalCapacity) * 1.1).toFixed(1) }} t</strong>
                </div>

                <div class="legend-item d-inline-flex align-items-center font-small-2">
                  <span class="legend-line-indicator legend-nominal mr-50" />
                  <span class="text-muted mr-25">{{ $t('Nominal Capacity (100%):') }}</span>
                  <strong class="text-primary">{{ Number(nominalCapacity).toFixed(1) }} t</strong>
                </div>

                <div class="legend-item d-inline-flex align-items-center font-small-2">
                  <span class="legend-line-indicator legend-underload mr-50" />
                  <span class="text-muted mr-25">{{ $t('Conforming Floor (90%):') }}</span>
                  <strong class="text-warning">{{ (Number(nominalCapacity) * 0.9).toFixed(1) }} t</strong>
                </div>

                <div class="legend-item d-inline-flex align-items-center font-small-2">
                  <span class="legend-dot-indicator legend-actual mr-50" />
                  <span class="text-muted mr-25">{{ $t('Estimated Cycle Load') }}</span>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="text-center py-4 text-muted">
                <b-avatar
                  variant="light-secondary"
                  size="48px"
                  class="mb-50"
                >
                  <feather-icon
                    icon="BarChart2Icon"
                    size="24"
                    class="text-muted"
                  />
                </b-avatar>
                <p class="font-small-3 mb-0">
                  {{ $t('No cycles recorded for this period.') }}
                </p>
              </div>
            </template>
          </div>

          <!-- TAB 3: QUALIDADE & ALERTAS -->
          <div v-else-if="activeTab === 3">
            <quality-alerts-card
              :quality="qualityData"
              :alerts="alertsList"
              @select-cycle="openCycleByNumber"
            />
          </div>
        </div>
      </b-card>

      <!-- SECTION 3: DETAILED CYCLES TABLE (PAGINATED) -->
      <b-card
        no-body
        class="asset-list-card shadow-sm border-0 mb-2"
      >
        <!-- Table Header Toolbar -->
        <div
          class="p-1 border-bottom d-flex flex-column flex-md-row align-items-md-center justify-content-between"
          style="gap: 12px;"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="DatabaseIcon"
              size="18"
              class="text-primary mr-50"
            />
            <div>
              <h6 class="mb-0 font-weight-bolder d-inline mr-50">
                {{ $t('Detail of Transport Cycles') }}
              </h6>
              <b-badge
                variant="light-primary"
                pill
              >
                {{ $t('{count} cycles in the period', { count: detailsData.totalItems }) }}
              </b-badge>
            </div>
          </div>

          <div
            class="d-flex align-items-center flex-wrap"
            style="gap: 12px;"
          >
            <!-- Per Page Selector -->
            <div class="d-flex align-items-center">
              <label class="mr-50 mb-0 font-small-3 text-muted">{{ $t('Display') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mr-50"
                @input="onPerPageChange"
              />
              <label class="mb-0 font-small-3 text-muted">{{ $t('per page') }}</label>
            </div>

            <!-- Refresh Table Button -->
            <b-button
              v-b-tooltip.hover
              variant="outline-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Reload Cycle Table')"
              @click="fetchDetails"
            >
              <feather-icon
                icon="RotateCwIcon"
                size="14"
                :class="{ 'spin-icon': isLoadingDetails }"
              />
            </b-button>
          </div>
        </div>

        <!-- Table Component -->
        <b-table
          ref="refCycleTable"
          class="position-relative mb-0 asset-data-table table-hover"
          tbody-tr-class="asset-table-row"
          :items="detailsData.data"
          responsive
          :fields="tableColumns"
          primary-key="number"
          show-empty
          :busy="isLoadingDetails"
        >
          <!-- Table Busy Skeleton -->
          <template #table-busy>
            <div class="py-1 px-1">
              <div
                v-for="n in 5"
                :key="'skeleton-row-' + n"
                class="p-1 mb-50 rounded bg-light-secondary"
              >
                <b-row class="align-items-center">
                  <b-col cols="1">
                    <b-skeleton
                      width="85%"
                      height="18px"
                      animation="wave"
                    />
                  </b-col>
                  <b-col cols="3">
                    <b-skeleton
                      width="70%"
                      height="18px"
                      animation="wave"
                    />
                  </b-col>
                  <b-col cols="2">
                    <b-skeleton
                      width="60%"
                      height="18px"
                      animation="wave"
                    />
                  </b-col>
                  <b-col cols="2">
                    <b-skeleton
                      width="75%"
                      height="18px"
                      animation="wave"
                    />
                  </b-col>
                  <b-col cols="2">
                    <b-skeleton
                      width="60%"
                      height="18px"
                      animation="wave"
                    />
                  </b-col>
                  <b-col cols="2">
                    <b-skeleton
                      width="80%"
                      height="22px"
                      animation="wave"
                    />
                  </b-col>
                </b-row>
              </div>
            </div>
          </template>

          <!-- Empty State -->
          <template #empty>
            <div class="text-center py-4 px-2">
              <b-avatar
                variant="light-secondary"
                size="64px"
                class="mb-1"
              >
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted"
                />
              </b-avatar>
              <h5 class="font-weight-bolder mb-50">
                {{ $t('No cycles recorded for the period') }}
              </h5>
              <p
                class="text-muted font-small-3 mb-1 mx-auto"
                style="max-width: 450px;"
              >
                {{ $t('No operational transport cycles were found with the filters applied.') }}
              </p>
              <b-button
                variant="outline-primary"
                size="sm"
                class="d-inline-flex align-items-center"
                @click="clearFilters"
              >
                <feather-icon
                  icon="RotateCcwIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Restore Filters') }}</span>
              </b-button>
            </div>
          </template>

          <!-- Column Head: Classificação -->
          <template #head(classification)>
            <div class="d-inline-flex align-items-center">
              <span>{{ $t('Classification') }}</span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="12"
                class="ml-25 text-muted cursor-pointer"
                :title="$t('Ranges: Underload (<90%), Compliant (90% to 110%), Overload (110% to 120%) and Severe (>120%). Omitted if nominal capacity is not reported.')"
                @click.stop="openGuideModal(2)"
              />
            </div>
          </template>

          <!-- Column Head: Alertas / Status -->
          <template #head(flags)>
            <div class="d-inline-flex align-items-center">
              <span>{{ $t('Alerts/Status') }}</span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="12"
                class="ml-25 text-muted cursor-pointer"
                :title="$t('Incomplete: the period cut the trip short, there was a long hole without reading or the discharge did not appear.')"
                @click.stop="openGuideModal(0)"
              />
            </div>
          </template>

          <!-- Column: Ciclo # -->
          <template #cell(number)="data">
            <span class="font-weight-bolder text-primary font-small-3">
              #{{ data.item.number }}
            </span>
          </template>

          <!-- Column: Horário Início / Fim -->
          <template #cell(start)="data">
            <div class="d-inline-flex flex-column font-small-2">
              <span class="font-weight-bold text-dark">
                {{ formatDateTimeDisplay(data.item.start) }}
              </span>
              <small class="text-muted">
                {{ $t('until') }} {{ formatDateTimeDisplay(data.item.end) }}
              </small>
            </div>
          </template>

          <!-- Column: Carga Estimada -->
          <template #cell(payloadTonnes)="data">
            <div class="d-inline-flex align-items-baseline font-weight-bolder text-dark">
              <span>{{ formatNumber(data.item.payloadTonnes) }}</span>
              <small class="text-muted ml-25 font-small-1">t</small>
            </div>
          </template>

          <!-- Column: Utilização % -->
          <template #cell(utilizationPercent)="data">
            <span
              v-if="data.item.utilizationPercent !== null"
              class="font-weight-bold font-small-2"
            >
              {{ formatNumber(data.item.utilizationPercent) }}%
            </span>
            <span
              v-else
              class="text-muted font-small-1"
            >—</span>
          </template>

          <!-- Column: Classificação -->
          <template #cell(classification)="data">
            <b-badge
              v-if="data.item.classification"
              :variant="resolveClassificationBadgeVariant(data.item.classification)"
              pill
              class="font-small-1 font-weight-bold"
            >
              {{ resolveClassificationLabel(data.item.classification) }}
            </b-badge>
            <span
              v-else
              class="text-muted font-small-1"
            >—</span>
          </template>

          <!-- Column: Duração Total -->
          <template #cell(cycleMinutes)="data">
            <div
              v-b-tooltip.hover
              :title="$t('Load: {load}m / Transport: {transport}m / Unload: {unload}m', { load: formatNumber(data.item.loadingMinutes), transport: formatNumber(data.item.transportMinutes), unload: formatNumber(data.item.unloadingMinutes) })"
              class="d-inline-flex align-items-center font-small-2 cursor-pointer"
            >
              <feather-icon
                icon="ClockIcon"
                size="12"
                class="mr-25 text-primary"
              />
              <span>{{ formatNumber(data.item.cycleMinutes) }} min</span>
            </div>
          </template>

          <!-- Column: Distância Carregado -->
          <template #cell(loadedDistanceKm)="data">
            <div class="d-inline-flex flex-column font-small-2">
              <span class="font-weight-bold text-dark">{{ formatNumber(data.item.loadedDistanceKm) }} km</span>
              <small class="text-muted">{{ $t('Avg:') }} {{ formatNumber(data.item.averageSpeedKmh) }} km/h</small>
            </div>
          </template>

          <!-- Column: Parado % -->
          <template #cell(stoppedTransportPercent)="data">
            <span
              v-b-tooltip.hover
              :class="data.item.stoppedTransportPercent > 20 ? 'text-danger font-weight-bold font-small-2' : 'font-small-2 text-dark'"
              :title="data.item.stoppedTransportPercent > 20 ? $t('Stopped time in transport above 20%') : ''"
            >
              {{ formatNumber(data.item.stoppedTransportPercent) }}%
            </span>
          </template>

          <!-- Column: Alertas / Status -->
          <template #cell(flags)="data">
            <div
              class="d-flex align-items-center flex-wrap"
              style="gap: 4px;"
            >
              <b-badge
                v-if="data.item.incomplete"
                v-b-tooltip.hover
                variant="light-danger"
                pill
                class="font-small-1 cursor-pointer"
                :title="$t('Incomplete cycle: the period cut the trip, there was a long hole without reading or the discharge did not appear')"
                @click="openGuideModal(0)"
              >
                {{ $t('Incomplete') }}
              </b-badge>
              <b-badge
                v-if="data.item.gpsLost"
                v-b-tooltip.hover
                variant="light-warning"
                pill
                class="font-small-1"
                :title="$t('Loss of GPS or altitude for more than 5 minutes')"
              >
                {{ $t('GPS') }}
              </b-badge>
              <b-badge
                v-if="data.item.dynamicPeakCount > 0"
                v-b-tooltip.hover
                variant="light-secondary"
                pill
                class="font-small-1"
                :title="$t('{count} dynamic peak(s) > 125%', { count: data.item.dynamicPeakCount })"
              >
                {{ data.item.dynamicPeakCount }} {{ $t('peaks') }}
              </b-badge>
              <span
                v-if="!data.item.incomplete && !data.item.gpsLost && (!data.item.dynamicPeakCount || data.item.dynamicPeakCount === 0)"
                class="text-muted font-small-1"
              >
                —
              </span>
            </div>
          </template>

          <!-- Column: Ações -->
          <template #cell(actions)="data">
            <b-button
              v-b-tooltip.hover
              variant="flat-primary"
              size="sm"
              class="btn-icon rounded-circle"
              :title="$t('View Full Cycle Profile')"
              @click="openCycleModal(data.item)"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
              />
            </b-button>
          </template>
        </b-table>

        <!-- Table Footer: Pagination & Counts -->
        <div
          v-if="detailsData.totalItems > 0"
          class="asset-list-footer p-1 border-top d-flex align-items-center justify-content-between flex-wrap"
          style="gap: 0.75rem;"
        >
          <div class="d-flex align-items-center font-small-3 text-muted">
            <span>
              {{ $t('Displaying {from} to {to} of {total} cycles', { from: paginationFrom, to: paginationTo, total: detailsData.totalItems }) }}
            </span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="detailsData.totalItems"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 pagination-modern"
            prev-class="prev-item"
            next-class="next-item"
            @input="onPageChange"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </b-card>

      <!-- Cycle Detail Modal -->
      <cycle-detail-modal
        v-model="showCycleModal"
        :cycle="selectedCycle"
        :nominal-capacity="nominalCapacity"
      />
    </template>

    <!-- Report Interpretation & Methodology Guide Modal -->
    <payload-report-guide-modal
      v-model="showGuideModal"
      :initial-tab="guideInitialTab"
    />
  </section>
</template>

<script>
/* eslint-disable import/no-cycle, no-param-reassign */
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { resolveIcon } from '@/utils/assetUtils'
import defaultCarIcon from '@/assets/images/assets/Car.png'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BAlert,
  BTable,
  BBadge,
  BPagination,
  BAvatar,
  BSpinner,
  BSkeleton,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BFormInput,
  BLink,
  BTabs,
  BTab,
  VBTooltip,
} from 'bootstrap-vue'

import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'
import vSelect from 'vue-select'
import VueApexCharts from 'vue-apexcharts'

import payloadAnalyticsStoreModule from './payloadAnalyticsStoreModule'
import CycleDetailModal from './components/CycleDetailModal.vue'
import PayloadZonesMap from './components/PayloadZonesMap.vue'
import QualityAlertsCard from './components/QualityAlertsCard.vue'
import PayloadReportGuideModal from './components/PayloadReportGuideModal.vue'

/**
 * Formata data no relógio local do usuário para envio ao backend (sem Z nem offset):
 * Início: YYYY-MM-DDT00:00:00
 * Fim: YYYY-MM-DDT23:59:59
 */
function formatToBackendDate(date, isEndOfDay = false) {
  if (!date) return ''
  let ymd = ''
  if (typeof date === 'string') {
    const match = date.match(/^\d{4}-\d{2}-\d{2}/)
    if (match) {
      [ymd] = match
    } else {
      const d = new Date(date)
      if (!Number.isNaN(d.getTime())) {
        const pad = n => String(n).padStart(2, '0')
        ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      }
    }
  } else if (date instanceof Date && !Number.isNaN(date.getTime())) {
    const pad = n => String(n).padStart(2, '0')
    ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  if (!ymd) return ''
  return isEndOfDay ? `${ymd}T23:59:59` : `${ymd}T00:00:00`
}

export default {
  name: 'PayloadAnalyticsReport',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BAlert,
    BTable,
    BBadge,
    BPagination,
    BAvatar,
    BSpinner,
    BSkeleton,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
    BLink,
    BTabs,
    BTab,
    flatPickr,
    vSelect,
    VueApexCharts,
    CycleDetailModal,
    PayloadZonesMap,
    QualityAlertsCard,
    PayloadReportGuideModal,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-payload-analytics'

    if (!store.hasModule(STORE_MODULE_NAME)) {
      store.registerModule(STORE_MODULE_NAME, payloadAnalyticsStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) {
        store.unregisterModule(STORE_MODULE_NAME)
      }
    })

    // UI state
    const showFilters = ref(false)
    const showEstimatedAlert = ref(true)
    const nominalCapacityInput = ref(null)
    const activeTab = ref(0)

    // Modal state
    const showCycleModal = ref(false)
    const selectedCycle = ref(null)
    const showGuideModal = ref(false)
    const guideInitialTab = ref(0)

    const openGuideModal = (tabIndex = 0) => {
      guideInitialTab.value = tabIndex
      showGuideModal.value = true
    }

    // Loadings
    const isLoadingSummary = ref(false)
    const isLoadingCycles = ref(false)
    const isLoadingDetails = ref(false)
    const loadingAsset = ref(false)

    // Asset Info & Identification
    const assetData = ref(null)
    const assetId = ref('')
    const assetOptions = ref([])
    const selectedAssetItem = ref(null)

    const routeAssetId = computed(() => (
      (root.$route.params && root.$route.params.id)
      || (root.$route.query && root.$route.query.id)
      || ''
    ))

    const assetPlate = computed(() => assetData.value?.plate || '')
    const assetChassis = computed(() => assetData.value?.chassisNumber || '')
    const assetManufacturer = computed(() => (
      assetData.value?.manufacturerName
      || assetData.value?.brandName
      || assetData.value?.manufacturer
      || ''
    ))
    const assetModel = computed(() => {
      if (!assetData.value) return ''
      const name = assetData.value.modelIdName || assetData.value.modelName || ''
      const year = assetData.value.modelYear ? `(${assetData.value.modelYear})` : ''
      return `${name} ${year}`.trim()
    })
    const assetAvatar = computed(() => {
      if (!assetData.value) return resolveIcon(null, null)
      return resolveIcon(assetData.value.assetType, assetData.value.iconUrl)
    })
    const assetGroupName = computed(() => (
      assetData.value?.assetGroupName
      || assetData.value?.groupName
      || ''
    ))
    const assetCustomerName = computed(() => (
      assetData.value?.customerName
      || assetData.value?.ownerName
      || ''
    ))

    // Filter fields
    const dateStart = ref('')
    const dateEnd = ref('')
    const nominalCapacity = ref('')
    const dateRangePreset = ref('today')

    // Table & Pagination
    const currentPage = ref(1)
    const perPage = ref(10)
    const perPageOptions = [10, 25, 50, 100]

    // Containers for endpoint responses
    const summaryData = ref(null)
    const cyclesResponse = ref({
      cycles: [],
      histogram: [],
      loadZones: [],
      unloadZones: [],
    })
    const detailsData = ref({
      data: [],
      length: 0,
      totalItems: 0,
    })

    // Columns for cycles details table
    const tableColumns = computed(() => [
      {
        key: 'number', label: '#', sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'start', label: root.$t('Start / End'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'payloadTonnes', label: root.$t('Load (t)'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'utilizationPercent', label: root.$t('Utilization (%)'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'classification', label: root.$t('Classification'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'cycleMinutes', label: root.$t('Duration'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'loadedDistanceKm', label: root.$t('Dist. Loaded'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'stoppedTransportPercent', label: root.$t('Stopped %'), sortable: true, thClass: 'text-nowrap',
      },
      {
        key: 'flags', label: root.$t('Alerts/Status'), sortable: false, thClass: 'text-nowrap',
      },
      {
        key: 'actions', label: root.$t('Actions'), sortable: false, thClass: 'text-center text-nowrap',
      },
    ])

    // FlatPickr configuration
    const flatPickrConfig = computed(() => ({
      locale: getFlatpickrLocale(),
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'd/m/Y',
    }))

    // Active filters count
    const activeFiltersCount = computed(() => {
      let count = 0
      if (dateStart.value) count += 1
      if (dateEnd.value) count += 1
      if (nominalCapacity.value) count += 1
      return count
    })

    const hasNominalCapacity = computed(() => (
      nominalCapacity.value !== null
      && nominalCapacity.value !== undefined
      && nominalCapacity.value !== ''
      && Number(nominalCapacity.value) > 0
    ))

    // Summary KPIs
    const kpis = computed(() => {
      if (summaryData.value && summaryData.value.kpis) {
        return summaryData.value.kpis
      }
      return {
        totalTransportedTonnes: 0,
        cycleCount: 0,
        averagePayloadTonnes: null,
        medianPayloadTonnes: null,
        variabilityPercent: null,
        complianceRate: null,
        capacityLossTonnes: null,
        excessAboveIdealTonnes: null,
        averageCycleMinutes: null,
        minCycleMinutes: null,
        maxCycleMinutes: null,
        averageEmptyReturnMinutes: null,
        averageIntervalMinutes: null,
        clockProductivityTonnesPerHour: null,
        effectiveProductivityTonnesPerHour: null,
      }
    })

    // Distribution
    const distribution = computed(() => (
      summaryData.value && summaryData.value.distribution ? summaryData.value.distribution : null
    ))

    // Quality Data
    const qualityData = computed(() => (
      summaryData.value && summaryData.value.quality ? summaryData.value.quality : null
    ))

    // Alerts List
    const alertsList = computed(() => (
      summaryData.value && summaryData.value.alerts ? summaryData.value.alerts : []
    ))

    // Cycles & Zones
    const allCyclesData = computed(() => (
      cyclesResponse.value && cyclesResponse.value.cycles ? cyclesResponse.value.cycles : []
    ))

    const histogramData = computed(() => (
      cyclesResponse.value && cyclesResponse.value.histogram ? cyclesResponse.value.histogram : []
    ))

    const loadZones = computed(() => (
      cyclesResponse.value && cyclesResponse.value.loadZones ? cyclesResponse.value.loadZones : []
    ))

    const unloadZones = computed(() => (
      cyclesResponse.value && cyclesResponse.value.unloadZones ? cyclesResponse.value.unloadZones : []
    ))

    const zonesTotalCount = computed(() => loadZones.value.length + unloadZones.value.length)

    // Pagination calculations
    const paginationFrom = computed(() => {
      if (detailsData.value.totalItems === 0) return 0
      return (currentPage.value - 1) * perPage.value + 1
    })

    const paginationTo = computed(() => {
      const to = currentPage.value * perPage.value
      return to > detailsData.value.totalItems ? detailsData.value.totalItems : to
    })

    // Number formatters
    const formatNumber = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '—'
      return Number(val).toLocaleString(getIntlLocale(), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      })
    }

    const formatInteger = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '0'
      return Number(val).toLocaleString(getIntlLocale())
    }

    const formatDateDisplay = dateStr => {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    }

    const formatDateTimeDisplay = isoStr => {
      if (!isoStr) return '—'
      const d = new Date(isoStr)
      if (Number.isNaN(d.getTime())) return isoStr
      return d.toLocaleString(getIntlLocale(), {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const getComplianceColorClass = rate => {
      if (rate >= 80) return 'text-success'
      if (rate >= 60) return 'text-warning'
      return 'text-danger'
    }

    const resolveClassificationBadgeVariant = classification => {
      if (!classification) return 'light-secondary'
      const s = String(classification).toLowerCase().replace(/[-_\s]/g, '')
      if (s === 'underload' || s === 'subcarga') return 'light-warning'
      if (s === 'optimal' || s === 'conforme' || s === 'normal') return 'light-success'
      if (s === 'overload' || s === 'sobrecarga') return 'light-danger'
      if (s === 'severeoverload' || s === 'sobrecargasevera') return 'severe-overload'
      return 'light-secondary'
    }

    const resolveClassificationLabel = classification => {
      if (!classification) return '—'
      const s = String(classification).toLowerCase().replace(/[-_\s]/g, '')
      if (s === 'underload' || s === 'subcarga') return root.$t('Underload (<90%)')
      if (s === 'optimal' || s === 'conforme' || s === 'normal') return root.$t('Compliant (90%-110%)')
      if (s === 'overload' || s === 'sobrecarga') return root.$t('Overload (110%-120%)')
      if (s === 'severeoverload' || s === 'sobrecargasevera') return root.$t('Severe Overload (>120%)')
      return classification
    }

    // Chart 1: Donut Chart (Distribution)
    const donutChartSeries = computed(() => {
      if (!distribution.value) return [0, 0, 0, 0]
      const u = distribution.value.underload ? distribution.value.underload.cycles : 0
      const o = distribution.value.optimal ? distribution.value.optimal.cycles : 0
      const ov = distribution.value.overload ? distribution.value.overload.cycles : 0
      const so = distribution.value.severeOverload ? distribution.value.severeOverload.cycles : 0
      return [u, o, ov, so]
    })

    const donutChartOptions = computed(() => ({
      chart: {
        type: 'donut',
        toolbar: { show: false },
      },
      labels: [
        root.$t('Underload (< 90%)'),
        root.$t('Compliant / Ideal (90% to 110%)'),
        root.$t('Overload (110% to 120%)'),
        root.$t('Severe Overload (> 120%)'),
      ],
      colors: ['#ff9f43', '#28c76f', '#ea5455', '#9b111e'],
      dataLabels: {
        enabled: true,
        formatter(val) {
          return `${val.toFixed(1)}%`
        },
        style: {
          fontSize: '13px',
          fontWeight: '600',
        },
        dropShadow: { enabled: false },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '13px',
        itemMargin: { horizontal: 10, vertical: 4 },
        labels: { colors: '#6e6b7b' },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                fontWeight: 600,
                color: '#5e5873',
                offsetY: -6,
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 700,
                color: '#5e5873',
                offsetY: 6,
                formatter(val) {
                  return Number(val).toLocaleString(getIntlLocale())
                },
              },
              total: {
                show: true,
                label: root.$t('Total Cycles'),
                fontSize: '13px',
                fontWeight: 600,
                color: '#6e6b7b',
                formatter: () => {
                  if (!distribution.value) return '0'
                  const u = distribution.value.underload ? distribution.value.underload.cycles : 0
                  const o = distribution.value.optimal ? distribution.value.optimal.cycles : 0
                  const ov = distribution.value.overload ? distribution.value.overload.cycles : 0
                  const so = distribution.value.severeOverload ? distribution.value.severeOverload.cycles : 0
                  return Number(u + o + ov + so).toLocaleString(getIntlLocale())
                },
              },
            },
          },
        },
      },
    }))

    // Chart 2: Histogram (Bar Chart)
    const histogramChartSeries = computed(() => {
      if (!histogramData.value || histogramData.value.length === 0) return []
      return [
        {
          name: root.$t('Cycles'),
          data: histogramData.value.map(h => h.cycles),
        },
      ]
    })

    const histogramChartOptions = computed(() => {
      const categories = histogramData.value.map(h => h.range)
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            distributed: true,
            borderRadius: 6,
            columnWidth: '40%',
          },
        },
        colors: ['#ff9f43', '#28c76f', '#ea5455', '#9b111e'],
        grid: {
          padding: { bottom: 15 },
        },
        xaxis: {
          categories,
          title: {
            text: root.$t('Weight Ranges (Tons)'),
            offsetY: 10,
            style: { color: '#6e6b7b', fontSize: '12px', fontWeight: 600 },
          },
          labels: {
            style: { colors: '#6e6b7b', fontSize: '11px', fontWeight: '500' },
          },
        },
        yaxis: {
          title: {
            text: root.$t('Cycles Performed'),
            style: { color: '#6e6b7b', fontSize: '12px', fontWeight: 600 },
          },
          labels: {
            formatter(val) {
              return Math.round(val)
            },
            style: { colors: '#6e6b7b', fontSize: '11px' },
          },
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter(val) {
            return root.$t('{count} cyc.', { count: val })
          },
          style: { fontSize: '11px', fontWeight: '600' },
        },
        tooltip: {
          y: {
            formatter(val) {
              return root.$t('{count} cycles', { count: val })
            },
          },
        },
      }
    })

    // Cycle Detail Modal Actions
    const openCycleModal = cycle => {
      selectedCycle.value = cycle
      showCycleModal.value = true
    }

    const openCycleByNumber = cycleNum => {
      if (!cycleNum) return
      // Search in allCyclesData first
      let cycle = allCyclesData.value.find(c => c.number === cycleNum)
      if (!cycle && detailsData.value.data) {
        cycle = detailsData.value.data.find(c => c.number === cycleNum)
      }
      if (cycle) {
        openCycleModal(cycle)
      } else {
        root.$bvToast.toast(root.$t('Loading details for Cycle #{number}...', { number: cycleNum }), {
          variant: 'info',
          solid: true,
        })
      }
    }

    // Chart 3: Chronological Cycles Timeline Chart & Scroll Handling
    const isFitToScreen = ref(false)
    const cyclesTimelineScrollContainer = ref(null)

    // Dynamic min-width for smooth horizontal scrolling when many points exist
    const cyclesTimelineChartMinWidth = computed(() => {
      if (isFitToScreen.value) return '100%'
      const count = (allCyclesData.value && allCyclesData.value.length) || 0
      if (count <= 16) return '100%'
      // Give ~44px per cycle so markers, hover hitboxes, and x-axis labels breathe comfortably
      return `${Math.max(850, count * 44)}px`
    })

    const scrollToStart = () => {
      if (cyclesTimelineScrollContainer.value) {
        cyclesTimelineScrollContainer.value.scrollTo({ left: 0, behavior: 'smooth' })
      }
    }

    const scrollToEnd = () => {
      if (cyclesTimelineScrollContainer.value) {
        cyclesTimelineScrollContainer.value.scrollTo({
          left: cyclesTimelineScrollContainer.value.scrollWidth,
          behavior: 'smooth',
        })
      }
    }

    const cyclesTimelineChartSeries = computed(() => {
      if (!allCyclesData.value || allCyclesData.value.length === 0) return []
      return [
        {
          name: root.$t('Estimated Load (t)'),
          type: 'line',
          data: allCyclesData.value.map(c => ({
            x: `#${c.number}`,
            y: c.payloadTonnes,
          })),
        },
      ]
    })

    const cyclesTimelineChartOptions = computed(() => {
      const nominal = hasNominalCapacity.value ? Number(nominalCapacity.value) : null
      const annotationsY = []
      const count = (allCyclesData.value && allCyclesData.value.length) || 0
      const isFit = isFitToScreen.value

      if (nominal) {
        annotationsY.push(
          {
            y: nominal,
            borderColor: '#7367f0',
            strokeDashArray: 3,
            label: {
              show: false,
              style: { color: 'transparent', background: 'transparent' },
              text: '',
            },
          },
          {
            y: nominal * 1.1,
            borderColor: '#ea5455',
            strokeDashArray: 4,
            label: {
              show: false,
              style: { color: 'transparent', background: 'transparent' },
              text: '',
            },
          },
          {
            y: nominal * 0.9,
            borderColor: '#ff9f43',
            strokeDashArray: 4,
            label: {
              show: false,
              style: { color: 'transparent', background: 'transparent' },
              text: '',
            },
          },
        )
      }

      // Dynamic marker and stroke dimensions based on mode & volume
      let markerSize = 5
      let strokeWidth = 2.5
      if (isFit && count > 100) {
        markerSize = 0 // Clean continuous line in macro view; markers reveal on hover
        strokeWidth = 2
      } else if (isFit && count > 40) {
        markerSize = 2.5
        strokeWidth = 2
      } else if (count > 80 && !isFit) {
        markerSize = 4
        strokeWidth = 2.5
      }

      return {
        chart: {
          type: 'line',
          toolbar: {
            show: true,
            tools: {
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
          events: {
            markerClick(event, chartContext, { dataPointIndex }) {
              if (allCyclesData.value && allCyclesData.value[dataPointIndex]) {
                openCycleModal(allCyclesData.value[dataPointIndex])
              }
            },
          },
          animations: {
            enabled: count <= 120,
            easing: 'easeinout',
            speed: 400,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: strokeWidth,
        },
        colors: ['#7367f0'],
        markers: {
          size: markerSize,
          colors: ['#7367f0'],
          strokeColors: '#ffffff',
          strokeWidth: markerSize > 0 ? 2 : 0,
          hover: { size: Math.max(6, markerSize + 2) },
        },
        annotations: {
          yaxis: annotationsY,
        },
        grid: {
          borderColor: '#ebe9f1',
          padding: {
            top: -10,
            bottom: 15,
            left: 15,
            right: 15,
          },
        },
        xaxis: {
          title: {
            text: root.$t('Cycles in Chronological Order'),
            style: { color: '#6e6b7b', fontSize: '12px', fontWeight: 600 },
          },
          tickAmount: isFit && count > 20 ? Math.min(16, count) : undefined,
          labels: {
            rotate: -45,
            rotateAlways: count > 10,
            hideOverlappingLabels: true,
            style: { colors: '#6e6b7b', fontSize: '10px' },
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          title: {
            text: root.$t('Estimated Load (t)'),
            style: { color: '#6e6b7b', fontSize: '12px', fontWeight: 600 },
          },
          labels: {
            formatter(val) {
              return val ? val.toFixed(1) : '0'
            },
            style: { colors: '#6e6b7b', fontSize: '11px' },
          },
        },
        tooltip: {
          custom({ dataPointIndex }) {
            const cycle = allCyclesData.value[dataPointIndex]
            if (!cycle) return ''
            const classLabel = resolveClassificationLabel(cycle.classification)
            const classVariant = resolveClassificationBadgeVariant(cycle.classification)
            const classHtml = cycle.classification
              ? `<span class="badge badge-${classVariant} font-small-1 font-weight-bold">${classLabel}</span>`
              : '<strong class="text-muted font-small-2">—</strong>'
            const cycleDate = cycle.start || cycle.startTime
            const dateStr = cycleDate ? formatDateTimeDisplay(cycleDate) : ''
            return `
              <div class="px-1 py-75 shadow rounded bg-white" style="border: 1px solid rgba(34, 41, 47, 0.1); font-family: inherit; min-width: 250px;">
                <div class="mb-50 pb-25 border-bottom">
                  <strong class="text-primary font-small-3">${root.$t('Cycle #{number}', { number: cycle.number })}</strong>
                  ${dateStr ? `<div class="text-muted font-small-1" style="font-size: 11px;">${dateStr}</div>` : ''}
                </div>
                <div class="d-flex justify-content-between mb-25 font-small-2">
                  <span class="text-muted">${root.$t('Estimated Load:')}</span>
                  <strong class="text-dark">${cycle.payloadTonnes.toFixed(2)} t</strong>
                </div>
                <div class="d-flex justify-content-between mb-25 font-small-2">
                  <span class="text-muted">${root.$t('Total Duration:')}</span>
                  <strong class="text-dark">${cycle.cycleMinutes ? cycle.cycleMinutes.toFixed(1) : '—'} min</strong>
                </div>
                <div class="d-flex justify-content-between align-items-center font-small-2">
                  <span class="text-muted">${root.$t('Classification:')}</span>
                  ${classHtml}
                </div>
                <div class="mt-50 pt-25 border-top text-center">
                  <small class="text-primary font-small-1 font-weight-bold">${root.$t('Click to see cycle details')}</small>
                </div>
              </div>
            `
          },
        },
      }
    })

    // Actions & Methods
    const showToastError = err => {
      const message = (err.response && err.response.data && (err.response.data.message || err.response.data.title))
        || err.message
        || root.$t('Error querying payload analytics data')
      root.$bvToast.toast(message, {
        title: root.$t('❌ Error'),
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
      })
    }

    const setDatePreset = preset => {
      dateRangePreset.value = preset
      const today = new Date()
      const pad = n => String(n).padStart(2, '0')
      const formatYMD = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

      if (preset === 'today') {
        dateStart.value = formatYMD(today)
        dateEnd.value = formatYMD(today)
      } else if (preset === 'yesterday') {
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        dateStart.value = formatYMD(yesterday)
        dateEnd.value = formatYMD(yesterday)
      } else if (preset === 'last7') {
        const start = new Date(today)
        start.setDate(today.getDate() - 6)
        dateStart.value = formatYMD(start)
        dateEnd.value = formatYMD(today)
      } else if (preset === 'month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1)
        dateStart.value = formatYMD(start)
        dateEnd.value = formatYMD(today)
      }
    }

    const onCustomDateChange = () => {
      dateRangePreset.value = 'custom'
    }

    const buildCommonParams = () => ({
      assetId: assetId.value,
      startDate: formatToBackendDate(dateStart.value, false),
      endDate: formatToBackendDate(dateEnd.value, true),
      nominalCapacity: hasNominalCapacity.value ? Number(nominalCapacity.value) : undefined,
    })

    const fetchSummary = () => {
      if (!assetId.value) return
      isLoadingSummary.value = true
      const params = buildCommonParams()

      store
        .dispatch(`${STORE_MODULE_NAME}/fetchSummary`, params)
        .then(response => {
          summaryData.value = response.data
        })
        .catch(err => {
          summaryData.value = null
          showToastError(err)
        })
        .finally(() => {
          isLoadingSummary.value = false
        })
    }

    const fetchCycles = () => {
      if (!assetId.value) return
      isLoadingCycles.value = true
      const params = buildCommonParams()

      store
        .dispatch(`${STORE_MODULE_NAME}/fetchCycles`, params)
        .then(response => {
          cyclesResponse.value = response.data || {
            cycles: [],
            histogram: [],
            loadZones: [],
            unloadZones: [],
          }
        })
        .catch(err => {
          cyclesResponse.value = {
            cycles: [], histogram: [], loadZones: [], unloadZones: [],
          }
          showToastError(err)
        })
        .finally(() => {
          isLoadingCycles.value = false
        })
    }

    const fetchDetails = () => {
      if (!assetId.value) return
      isLoadingDetails.value = true
      const params = {
        ...buildCommonParams(),
        page: currentPage.value,
        perPage: perPage.value,
      }

      store
        .dispatch(`${STORE_MODULE_NAME}/fetchDetails`, params)
        .then(response => {
          if (response && response.data) {
            detailsData.value = {
              data: response.data.data || [],
              length: response.data.length || 0,
              totalItems: response.data.totalItems || 0,
            }
          }
        })
        .catch(err => {
          detailsData.value = { data: [], length: 0, totalItems: 0 }
          showToastError(err)
        })
        .finally(() => {
          isLoadingDetails.value = false
        })
    }

    const fetchData = () => {
      if (!assetId.value) {
        root.$bvToast.toast(root.$t('Please select a vehicle to generate the report'), {
          title: root.$t('⚠️ Attention'),
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        })
        showFilters.value = true
        return
      }

      if (!dateStart.value || !dateEnd.value) {
        root.$bvToast.toast(root.$t('Please set the start and end dates of the period'), {
          title: root.$t('⚠️ Attention'),
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        })
        return
      }

      currentPage.value = 1
      fetchSummary()
      fetchCycles()
      fetchDetails()
    }

    const clearFilters = () => {
      nominalCapacity.value = ''
      perPage.value = 10
      currentPage.value = 1
      setDatePreset('today')
      fetchData()
    }

    const clearNominalCapacity = () => {
      nominalCapacity.value = ''
      fetchData()
    }

    const focusNominalCapacity = () => {
      showFilters.value = true
      root.$nextTick(() => {
        if (nominalCapacityInput.value && nominalCapacityInput.value.$el) {
          nominalCapacityInput.value.$el.focus()
        }
      })
    }

    const onPageChange = val => {
      currentPage.value = val
      fetchDetails()
    }

    const onPerPageChange = val => {
      perPage.value = val
      currentPage.value = 1
      fetchDetails()
    }

    const goToAssetSummary = () => {
      if (assetId.value) {
        root.$router.push({ name: 'apps-asset-summary', params: { id: assetId.value } })
      }
    }

    const goBack = () => {
      root.$router.back()
    }

    const handleImageError = event => {
      event.target.src = defaultCarIcon
    }

    const copyToClipboard = (text, label) => {
      if (!text) return
      const successTitle = `${label || root.$t('text')} ${root.$t('copiedToClipboard')}!`
      const failTitle = root.$t('failedToCopyToClipboard')

      const notifySuccess = () => {
        root.$toast({
          component: ToastificationContent,
          props: {
            title: successTitle,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      }

      const notifyError = () => {
        root.$toast({
          component: ToastificationContent,
          props: {
            title: failTitle,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }

      if (root.$copyText) {
        root.$copyText(text).then(notifySuccess).catch(notifyError)
      } else if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(notifySuccess).catch(notifyError)
      }
    }

    // Asset Loading
    const loadAssetData = id => {
      if (!id) return Promise.resolve(null)
      loadingAsset.value = true
      return store
        .dispatch(`${STORE_MODULE_NAME}/fetchAsset`, { id })
        .then(response => {
          if (response && response.data) {
            assetData.value = response.data
          }
          return response
        })
        .catch(() => {})
        .finally(() => {
          loadingAsset.value = false
        })
    }

    // Asset Search (when no asset in URL)
    let searchTimeout = null
    const onAssetSearch = (search, loading) => {
      if (search.length < 2) return
      loading(true)
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        store
          .dispatch(`${STORE_MODULE_NAME}/fetchAssets`, { q: search, perPage: 15 })
          .then(res => {
            const list = res.data && res.data.data ? res.data.data : res.data || []
            assetOptions.value = list.map(a => ({
              ...a,
              label: `${a.modelName || a.manufacturerName || a.chassisNumber || a.id}${a.chassisNumber ? ` (${a.chassisNumber})` : ''}`.trim(),
            }))
          })
          .catch(() => {})
          .finally(() => {
            loading(false)
          })
      }, 350)
    }

    const onAssetSelect = val => {
      if (val && val.id) {
        assetId.value = val.id
        assetData.value = val
        fetchData()
      }
    }

    // CSV Export
    const exportToCsv = () => {
      if (!detailsData.value.data || detailsData.value.data.length === 0) return
      const headers = [
        root.$t('Cycle'),
        root.$t('Start'),
        root.$t('End'),
        root.$t('Estimated Load (t)'),
        root.$t('Utilization (%)'),
        root.$t('Classification'),
        root.$t('Total Duration (min)'),
        root.$t('Loading (min)'),
        root.$t('Transport (min)'),
        root.$t('Unloading (min)'),
        root.$t('Empty Return (min)'),
        root.$t('Loaded Distance (km)'),
        root.$t('Average Speed (km/h)'),
        root.$t('Maximum Speed (km/h)'),
        root.$t('Stopped (%)'),
        root.$t('Elevation Change (m)'),
        root.$t('Gradient (%)'),
        root.$t('Loader Passes'),
        root.$t('Residue (t)'),
        root.$t('Incomplete'),
        root.$t('GPS Lost'),
      ]
      const rows = detailsData.value.data.map(item => [
        item.number,
        `"${item.start}"`,
        `"${item.end}"`,
        item.payloadTonnes,
        item.utilizationPercent !== null ? item.utilizationPercent : '',
        `"${resolveClassificationLabel(item.classification) || ''}"`,
        item.cycleMinutes,
        item.loadingMinutes,
        item.transportMinutes,
        item.unloadingMinutes,
        item.emptyReturnMinutes !== null ? item.emptyReturnMinutes : '',
        item.loadedDistanceKm,
        item.averageSpeedKmh,
        item.maxSpeedKmh,
        item.stoppedTransportPercent,
        item.ascentMeters,
        item.gradientPercent,
        item.loaderPassesMinimum !== null ? item.loaderPassesMinimum : '',
        item.residueTonnes,
        item.incomplete ? root.$t('Yes') : root.$t('No'),
        item.gpsLost ? root.$t('Yes') : root.$t('No'),
      ])

      const csvContent = `data:text/csv;charset=utf-8,\uFEFF${[headers.join(';'), ...rows.map(e => e.join(';'))].join('\n')}`
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `payload_analytics_${assetModel.value || assetChassis.value || assetId.value || 'asset'}_${dateStart.value}_${dateEnd.value}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    // Lifecycle
    onMounted(() => {
      setDatePreset('today')
      const id = routeAssetId.value
      if (id) {
        assetId.value = id
        loadAssetData(id).then(() => {
          fetchData()
        })
      }
    })

    watch(
      () => routeAssetId.value,
      newId => {
        if (newId && newId !== assetId.value) {
          assetId.value = newId
          loadAssetData(newId).then(() => {
            fetchData()
          })
        }
      },
    )

    return {
      showFilters,
      showEstimatedAlert,
      nominalCapacityInput,
      activeTab,
      showCycleModal,
      selectedCycle,
      isLoadingSummary,
      isLoadingCycles,
      isLoadingDetails,
      loadingAsset,
      assetId,
      assetData,
      assetPlate,
      assetChassis,
      assetManufacturer,
      assetModel,
      assetAvatar,
      assetGroupName,
      assetCustomerName,
      assetOptions,
      selectedAssetItem,
      dateStart,
      dateEnd,
      nominalCapacity,
      dateRangePreset,
      currentPage,
      perPage,
      perPageOptions,
      summaryData,
      kpis,
      distribution,
      qualityData,
      alertsList,
      allCyclesData,
      histogramData,
      loadZones,
      unloadZones,
      zonesTotalCount,
      detailsData,
      tableColumns,
      flatPickrConfig,
      activeFiltersCount,
      hasNominalCapacity,
      paginationFrom,
      paginationTo,
      donutChartSeries,
      donutChartOptions,
      histogramChartSeries,
      histogramChartOptions,
      cyclesTimelineChartSeries,
      cyclesTimelineChartOptions,
      isFitToScreen,
      cyclesTimelineScrollContainer,
      cyclesTimelineChartMinWidth,
      scrollToStart,
      scrollToEnd,
      formatNumber,
      formatInteger,
      formatDateDisplay,
      formatDateTimeDisplay,
      getComplianceColorClass,
      resolveClassificationBadgeVariant,
      resolveClassificationLabel,
      setDatePreset,
      onCustomDateChange,
      fetchData,
      fetchDetails,
      clearFilters,
      clearNominalCapacity,
      focusNominalCapacity,
      openCycleModal,
      openCycleByNumber,
      onPageChange,
      onPerPageChange,
      goToAssetSummary,
      goBack,
      handleImageError,
      copyToClipboard,
      onAssetSearch,
      onAssetSelect,
      exportToCsv,
      showGuideModal,
      guideInitialTab,
      openGuideModal,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/map-leaflet.scss';
</style>

<style lang="scss">
.payload-analytics-report-wrapper {
  .spin-icon {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }

  /* Compact Asset Info Header Card */
  .asset-header-card {
    border: 1px solid rgba(115, 103, 240, 0.14) !important;
    border-left: 4px solid #7367f0 !important;
    border-radius: 0.5rem;
    background-color: #ffffff;
    transition: all 0.2s ease-in-out;

    .asset-header-content {
      min-height: 60px;
      display: flex;
      align-items: center;
    }

    .back-btn {
      border: 1px solid rgba(0, 0, 0, 0.08);
      transition: all 0.2s ease;
      &:hover {
        background-color: rgba(115, 103, 240, 0.1) !important;
        color: #7367f0 !important;
        border-color: #7367f0;
      }
    }

    .asset-avatar-box {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

      .asset-avatar-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 4px;
        transition: transform 0.2s ease;
      }

      &:hover .asset-avatar-img {
        transform: scale(1.08);
      }
    }

    .license-plate-badge {
      display: inline-flex;
      align-items: center;
      background: #ffffff;
      border: 2px solid #2b2b2b;
      border-radius: 6px;
      padding: 3px 10px;
      font-size: 0.95rem;
      font-weight: 800;
      letter-spacing: 1px;
      color: #2b2b2b;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      transition: all 0.15s ease;

      .plate-number {
        font-family: 'Consolas', 'Courier New', monospace;
      }

      .copy-icon {
        opacity: 0.6;
        transition: opacity 0.15s ease;
      }

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
        background-color: rgba(115, 103, 240, 0.05);

        .copy-icon {
          opacity: 1;
          color: #7367f0 !important;
        }
      }
    }

    .asset-brand-text {
      font-size: 1.05rem;
      letter-spacing: -0.2px;
      color: #7367f0 !important;
    }

    .asset-model-title {
      font-size: 1.05rem;
      letter-spacing: -0.2px;
    }

    .meta-chip {
      display: inline-flex;
      align-items: center;
      background: #f8f9fa;
      border: 1px solid #ebedf0;
      border-radius: 20px;
      padding: 3px 10px;
      font-size: 0.8rem;
      transition: all 0.15s ease;
      cursor: default;

      &.cursor-pointer, &[class*="cursor-pointer"] {
        cursor: pointer;
        &:hover {
          background: #f0f2f5;
          border-color: #7367f0;
        }
      }

      .meta-label {
        font-weight: 600;
        color: #6e6b7b;
        margin-right: 4px;
      }

      .meta-value {
        font-weight: 500;
        color: #5e5873;
      }

      .copy-icon {
        opacity: 0.6;
        transition: opacity 0.15s ease;
      }

      &:hover .copy-icon {
        opacity: 1;
        color: #7367f0 !important;
      }
    }

    .btn-view-details {
      font-weight: 600;
      border-radius: 6px;
      padding: 0.45rem 0.85rem;
      display: inline-flex;
      align-items: center;
      transition: all 0.2s ease;
    }
  }

  // Dark Layout Support for Asset Header Card
  body.dark-layout &,
  .dark-layout & {
    .asset-header-card {
      background-color: #283046 !important;
      border-color: rgba(255, 255, 255, 0.08) !important;
      border-left-color: #7367f0 !important;

      .back-btn {
        border-color: rgba(255, 255, 255, 0.12);
        color: #b4b7bd;
      }

      .asset-avatar-box {
        background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
        border-color: rgba(255, 255, 255, 0.1);
      }

      .license-plate-badge {
        background: #161d31;
        border-color: #4b4b5a;
        color: #d0d2d6;

        &:hover {
          border-color: #7367f0;
          color: #7367f0;
          background-color: rgba(115, 103, 240, 0.1);
        }
      }

      .asset-brand-text {
        color: #9086f4 !important;
      }

      .asset-model-title {
        color: #d0d2d6 !important;
      }

      .meta-chip {
        background: #161d31;
        border-color: rgba(255, 255, 255, 0.08);

        &.cursor-pointer:hover {
          background: #1e2433;
          border-color: #7367f0;
        }

        .meta-label {
          color: #b4b7bd;
        }

        .meta-value {
          color: #d0d2d6;
        }
      }
    }

    .cycles-timeline-scroll-container {
      &::-webkit-scrollbar-track {
        background: #161d31;
      }

      &::-webkit-scrollbar-thumb {
        background: #3b4253;

        &:hover {
          background: #4b5468;
        }
      }
    }

    .chart-limits-legend {
      .legend-item {
        background: #283046;
        border-color: rgba(255, 255, 255, 0.08);

        &:hover {
          background: #1e2433;
        }
      }
    }

    .cycles-timeline-chart-wrapper {
      .apexcharts-menu {
        background: #283046;
        border-color: rgba(255, 255, 255, 0.12);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);

        .apexcharts-menu-item {
          color: #d0d2d6;

          &:hover {
            background: #1e2433;
          }
        }
      }
    }
  }

  /* KPI Cards */
  .kpi-card {
    border-radius: 0.5rem;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 14px 0 rgba(34, 41, 47, 0.1) !important;
    }

    .kpi-unit {
      font-size: 0.8rem;
      font-weight: 500;
      color: #b9b9c3;
    }
  }

  /* Chart Limits Legend (Below Line Chart) */
  .chart-limits-legend {
    .legend-item {
      background: #f8f8f8;
      border: 1px solid #ebe9f1;
      padding: 0.35rem 0.75rem;
      border-radius: 20px;
      transition: background 0.2s ease;

      &:hover {
        background: #f0f0f5;
      }
    }

    .legend-line-indicator {
      display: inline-block;
      width: 22px;
      height: 3px;
      border-radius: 2px;

      &.legend-overload {
        background-color: #ea5455;
        border-top: 1px dashed #ea5455;
      }
      &.legend-nominal {
        background-color: #7367f0;
      }
      &.legend-underload {
        background-color: #ff9f43;
        border-top: 1px dashed #ff9f43;
      }
    }

    .legend-dot-indicator {
      display: inline-block;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #7367f0;
      border: 2px solid #fff;
      box-shadow: 0 0 0 1px #7367f0;
    }
  }

  /* Cycles Timeline Controls & Scroll Container */
  .cycles-timeline-controls {
    .btn-group {
      .btn {
        padding: 0.25rem 0.55rem;
      }
    }
  }

  .cycles-timeline-scroll-container {
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 8px;
    position: relative;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 7px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d0d2d6;
      border-radius: 4px;

      &:hover {
        background: #b4b7bd;
      }
    }
  }

  .cycles-timeline-chart-wrapper {
    position: relative;

    .apexcharts-toolbar {
      position: sticky !important;
      right: 8px !important;
      top: 4px !important;
      z-index: 12;

      // A toolbar fica no fluxo, no fim do gráfico. O menu padrão abre para baixo
      // e é cortado pelo overflow-y do container de rolagem, que termina logo abaixo.
      .apexcharts-menu {
        top: auto !important;
        bottom: calc(100% + 6px) !important;
        right: 0 !important;
        z-index: 20;
        box-shadow: 0 4px 16px rgba(34, 41, 47, 0.12);
      }
    }
  }

  /* Responsive Improvements for Mobile and Tablet */
  @media (max-width: 767.98px) {
    .nav-tabs,
    .nav-pills {
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 4px;

      &::-webkit-scrollbar {
        height: 3px;
      }
      &::-webkit-scrollbar-thumb {
        background: #ebe9f1;
        border-radius: 3px;
      }

      .nav-item {
        flex-shrink: 0;
      }

      .nav-link {
        white-space: nowrap;
        font-size: 0.82rem;
        padding: 0.5rem 0.75rem;
      }
    }

    .cycles-timeline-chart-wrapper {
      .apexcharts-toolbar {
        position: sticky !important;
        top: 0 !important;
        right: 6px !important;
      }
    }
  }

  @media (max-width: 575.98px) {
    .kpi-card {
      padding: 0.65rem 0.75rem !important;

      .kpi-label {
        font-size: 0.68rem !important;
      }

      h3 {
        font-size: 1.2rem !important;
      }

      .b-avatar {
        width: 34px !important;
        height: 34px !important;

        .feather {
          width: 16px !important;
          height: 16px !important;
        }
      }

      .kpi-footer {
        font-size: 0.65rem !important;
      }
    }

    .chart-limits-legend {
      gap: 8px !important;

      .legend-item {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem !important;
      }
    }

    .asset-list-footer {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
    }
  }

  /* Severe Overload Style */
  .severe-overload {
    background-color: #ffe5e5 !important;
    border-color: #9b111e !important;
  }
  .text-severe {
    color: #9b111e !important;
  }
  .badge-severe-overload {
    background-color: #9b111e !important;
    color: #fff !important;
  }

  /* Table styling */
  .asset-data-table {
    th {
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #5e5873;
      background-color: #f8f8f8;
      border-top: none;
    }
    td {
      vertical-align: middle;
    }
  }
}
</style>
